import React, { useCallback, useMemo } from 'react';
import * as Yup from 'yup';
import { Form, Formik, FormikHelpers, FormikConsumer } from 'formik';

import {
  ConsentCheckbox,
  CountryKeySelect,
  FormikError,
  Input,
  PhoneNumberInput,
  YupValidators,
} from '@app/form';
import { getLocale } from '@app/i18n';
import { Captcha, QuestionTooltip, useModalState, useToast } from '@app/core';
import {
  useFingerprint,
  useTrackSignUp,
  useTrackingParams,
} from '@app/analytics';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';

import {
  Account,
  ApiException,
  ApiValidationFailedException,
  Consent,
  ConsentName,
  hasConsents,
  Participant,
  ParticipantSource,
  useAcceptConsent,
  useCurrentParticipant,
  useHasConsents,
  useMe,
  useRegisterParticipant,
  useSignup,
  useUpdateParticipant,
} from '@app/api';

import { LoginViaCodeModal } from './LoginViaCodeModal';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';

const useStyles = createUseStyles((theme: Theme) => ({
  sameLine: {
    marginTop: theme.marginBase * 3,
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase * 2,
    flexWrap: 'wrap',
  },
  lineInput: {
    flex: 1,
    minWidth: 180,
  },
  sameLineLabel: {
    display: 'flex',
    gap: theme.marginBase / 2,
  },
  captcha: {
    marginBottom: theme.marginBase * 2,
  },
}));

type Values = {
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string | null;
  countryKey: string;
  consents: Consent[];
  altcha: string;
};

export const isMisingParticipantInformation = async (
  participant: Participant,
  account: Account,
  requirePhoneNumber: boolean,
) => {
  const hasAllConsents = await hasConsents(
    [
      ConsentName.PARTICIPANT_PRIVACY,
      ConsentName.PARTICIPANT_TERMS,
      ConsentName.PARTICIPANT_RULES,
    ],
    account.consents || [],
  );
  return (
    !participant.firstName ||
    !participant.lastName ||
    !participant.email ||
    (requirePhoneNumber ? !participant.phoneNumber : false) ||
    !participant.countryKey ||
    !hasAllConsents
  );
};

const accountToValues = (
  participant?: Participant,
  account?: Account,
  countryKey?: string,
): Values => ({
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  ...participant,
  ...account,
  countryKey: countryKey || participant?.countryKey || '',
  consents: [],
  altcha: '',
});

interface Props {
  onSuccess?: (participant: Participant) => any;
  sessionId?: string;
  children: React.ReactNode;
  askForPhone?: boolean;
  phoneIsOptional?: boolean;
  participantId?: string;
  trackingContext?: 'organic' | 'session';
  countryKey?: string;
}

export const SignupParticipantForm = ({
  onSuccess,
  sessionId,
  children,
  askForPhone,
  phoneIsOptional,
  participantId,
  trackingContext,
  countryKey,
}: Props) => {
  const toast = useToast();
  const tracking = useTrackingParams();
  const fingerprint = useFingerprint();
  const classes = useStyles();
  const { t, i18n } = useTranslation('common');

  const { data: me, isLoading: isMeLoading, isFetched } = useMe(true);
  const { data: participant, isLoading } = useCurrentParticipant(true);
  const isMeFirstLoading = (isMeLoading || isLoading) && !isFetched;

  const { mutateAsync: registerParticipant } = useRegisterParticipant();
  const { mutateAsync: updateParticipant } = useUpdateParticipant();

  const [loginCodeEmail, openCodeModal, closeCodeModal] = useModalState<
    string | undefined
  >(undefined);

  const initialValues = useMemo(
    () => accountToValues(participant, me, countryKey),
    [participant, me, countryKey],
  );

  const { mutateAsync: signup } = useSignup();
  const [savedValues, saveValues] = React.useState<Values | undefined>();
  const { mutate: acceptConsent } = useAcceptConsent();
  const trackSignup = useTrackSignUp();

  const hasTermsConsent = useHasConsents([
    ConsentName.PARTICIPANT_TERMS,
    ConsentName.PARTICIPANT_PRIVACY,
  ]);
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        firstName: Yup.string().required(),
        lastName: Yup.string().required(),
        email: Yup.string().required().email(),
        phoneNumber: askForPhone
          ? YupValidators.isPhoneNumber(phoneIsOptional)
          : Yup.string().optional().nullable(),
        consents: hasTermsConsent ? Yup.array() : Yup.array().required().min(2),
      }),
    [askForPhone, hasTermsConsent, phoneIsOptional],
  );

  const onLogin = useCallback(
    async (account: Account, _values?: Values) => {
      const values = _values || savedValues;
      if (!values) {
        return;
      }

      let participant: Participant;
      if (!account.linkedParticipants?.length) {
        participant = await registerParticipant({
          id: participantId,
          locale: getLocale(),
          source: sessionId
            ? ParticipantSource.SESSION
            : ParticipantSource.STANDALONE_FORM,
          sourceId: sessionId,
          tracking,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phoneNumber: values.phoneNumber || undefined,
          countryKey: values.countryKey,
          fingerprint,
          altcha: values.altcha,
        });
      } else {
        const id = account.linkedParticipants[0];
        participant = await updateParticipant({
          id,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email || me?.email,
          phoneNumber: values.phoneNumber || undefined,
          countryKey: values.countryKey,
        });
      }
      if (onSuccess) {
        await onSuccess(participant);
      }
    },
    [
      updateParticipant,
      registerParticipant,
      sessionId,
      tracking,
      fingerprint,
      onSuccess,
      savedValues,
      participantId,
      me,
    ],
  );

  const submit = useCallback(
    async (values: Values, helpers: FormikHelpers<Values>) => {
      try {
        let account = me;

        if (!account) {
          account = await signup({
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            altcha: values.altcha,
          });
          trackSignup({
            ...account,
            phoneNumber: values.phoneNumber as string,
            type: trackingContext || 'organic',
            role: 'participant',
          });
        }

        for (const consent of values.consents) {
          acceptConsent(consent);
        }

        await onLogin(account, {
          ...values,
          phoneNumber:
            values.phoneNumber && values.phoneNumber.length < 6
              ? null
              : values.phoneNumber,
        });
      } catch (e) {
        if (e instanceof ApiValidationFailedException) {
          helpers.setStatus(e.getFormikErrors());
        } else if (e instanceof ApiException) {
          if (e.response?.status === 409) {
            saveValues(values);
            openCodeModal(values.email);
            return;
          }
        }
        toast.exception(e);
      }
    },
    [
      me,
      onLogin,
      trackSignup,
      signup,
      acceptConsent,
      toast,
      openCodeModal,
      trackingContext,
    ],
  );

  if (isMeFirstLoading) {
    return null;
  }

  return (
    <div>
      <Formik
        initialValues={initialValues}
        onSubmit={submit}
        validationSchema={validationSchema}
      >
        <Form>
          <div className={classes.sameLine}>
            <Input
              name="firstName"
              label={t('participants.form.firstName')}
              placeholder={t('participants.placeholder.firstName')}
              noMargin
              containerClass={classes.lineInput}
            />
            <Input
              name="lastName"
              label={t('participants.form.lastName')}
              placeholder={t('participants.placeholder.lastName')}
              noMargin
              containerClass={classes.lineInput}
            />
          </div>
          <Input
            name="email"
            label={t('participants.form.email')}
            placeholder={t('participants.placeholder.email')}
            type="email"
            disabled={!!me}
          />

          {askForPhone && (
            <FormikConsumer>
              {({ setFieldValue }) => (
                <PhoneNumberInput
                  name="phoneNumber"
                  label={t('participants.form.phoneNumber')}
                  onCountryChange={(key: string) =>
                    setFieldValue('countryKey', key)
                  }
                />
              )}
            </FormikConsumer>
          )}

          <CountryKeySelect
            name="countryKey"
            label={
              <div className={classes.sameLineLabel}>
                {t('participants.form.countryKey')}
                <QuestionTooltip
                  title={t('participants.form.countryKeyTooltip')}
                  placement="right"
                />
              </div>
            }
          />

          <ConsentCheckbox name="consents" consent="PARTICIPANT_SIGNUP" />
          {!me?.linkedParticipants?.length && (
            <Captcha className={classes.captcha} invisible />
          )}
          <FormikError />
          {children}
        </Form>
      </Formik>
      <LoginViaCodeModal
        onRequestClose={closeCodeModal}
        isOpen={!!loginCodeEmail}
        email={loginCodeEmail}
        onSuccess={onLogin}
      />
    </div>
  );
};
